import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import MainContent from "./MainContent";
import { adminApiRequest } from "../utils/adminApi";
import { CalendarContext } from "../utils/CalendarContext";
import { checkStoreStatus } from "../utils/storeHours";
import Modal from "./Modal";

const Layout = ({ username, onLogout }) => {
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem("admin_activeTab") || "orders";
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [restaurantOpen, setRestaurantOpen] = useState(true);
  const [loadingRestaurantStatus, setLoadingRestaurantStatus] = useState(true);
  const [togglingRestaurantStatus, setTogglingRestaurantStatus] =
    useState(false);
  const [calendarTargetDate, setCalendarTargetDate] = useState(null);
  const [backendIsOpen, setBackendIsOpen] = useState(1);
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);

  const updateRestaurantState = (resIsOpen) => {
    setBackendIsOpen(resIsOpen);
    const { isTakeawayOpen } = checkStoreStatus();
    if (resIsOpen === 2) {
      setRestaurantOpen(true);
    } else if (resIsOpen === 1) {
      setRestaurantOpen(isTakeawayOpen);
    } else {
      setRestaurantOpen(false);
    }
  };

  useEffect(() => {
    sessionStorage.setItem("admin_activeTab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    const fetchRestaurantStatus = async () => {
      try {
        const res = await adminApiRequest({
          script: "get-restaurant-status",
        });

        if (res?.success) {
          updateRestaurantState(Number(res.is_open));
        }
      } catch (error) {
        console.error("Failed to fetch restaurant status:", error);
      } finally {
        setLoadingRestaurantStatus(false);
      }
    };

    fetchRestaurantStatus();
  }, []);

  const performToggle = async (forceStatus) => {
    setTogglingRestaurantStatus(true);
    try {
      const res = await adminApiRequest({
        script: "toggle-restaurant-status",
        data: {
          is_open: forceStatus,
        },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update restaurant status");
      }

      updateRestaurantState(Number(res.is_open));
    } finally {
      setTogglingRestaurantStatus(false);
      setShowOverrideWarning(false);
    }
  };

  const handleRestaurantToggle = async () => {
    if (togglingRestaurantStatus) return;

    const { isTakeawayOpen } = checkStoreStatus();

    if (restaurantOpen) {
      // Toggling OFF
      if (backendIsOpen === 2) {
        // Was forced open. Return to auto
        await performToggle(1);
      } else {
        // Was auto open. Force close it.
        await performToggle(0);
      }
    } else {
      // Toggling ON
      if (!isTakeawayOpen) {
        // Auto schedule says closed. Trying to force open.
        setShowOverrideWarning(true);
      } else {
        // Auto schedule says open. Return to auto.
        await performToggle(1);
      }
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  };

  // Listen for calendar navigation events from HallRentals
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.date) {
        setCalendarTargetDate(e.detail.date);
        setActiveTab("calendar");
      }
    };
    window.addEventListener("calendar:navigate", handler);
    return () => window.removeEventListener("calendar:navigate", handler);
  }, []);

  return (
    <CalendarContext.Provider value={{ calendarTargetDate, setCalendarTargetDate }}>
      <div className="h-[100dvh] w-full overflow-hidden bg-slate-100">
        <div className="flex h-full w-full">
          <Sidebar
            username={username}
            activeTab={activeTab}
            handleTabClick={handleTabClick}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            onLogout={onLogout}
            restaurantOpen={restaurantOpen}
            onRestaurantToggle={handleRestaurantToggle}
            loadingRestaurantStatus={loadingRestaurantStatus}
          />

          <MainContent
            activeTab={activeTab}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        </div>
      </div>

      {showOverrideWarning && (
        <Modal
          title="Manual Override Warning"
          onClose={() => setShowOverrideWarning(false)}
          onSubmit={(e) => {
            e.preventDefault();
            performToggle(2);
          }}
          submitButtonText="Force Open"
          showCancel={true}
          showSubmitButton={true}
          submitButtonClass="bg-red-600 hover:bg-red-700 text-white"
        >
          <p className="text-sm text-slate-700">
            The restaurant is currently scheduled to be <strong>closed</strong>. 
            By forcing it open, it will remain open and <strong>will not close automatically</strong> until you manually turn it off.
          </p>
          <p className="text-sm text-slate-700 font-semibold mt-4">
            Are you sure you want to force it open?
          </p>
        </Modal>
      )}
    </CalendarContext.Provider>
  );
};

export default Layout;
